import React from "react";
import {
  FileText,
  ClipboardList,
  BookOpen,
  Receipt,
  HardHat,
  Users,
  Check,
} from "lucide-react";

const services = [
  {
    icon: FileText,
    title: "Company Accounts & CT",
    desc: "Year-end statutory accounts and Corporation Tax returns filed accurately and on time.",
    items: [
      "Annual accounts to Companies House",
      "CT600 to HMRC",
      "Director's report & disclosures",
      "Tax efficiency review",
    ],
  },
  {
    icon: ClipboardList,
    title: "Self Assessment Tax Returns",
    desc: "Personal tax returns for directors, sole traders, landlords and high earners.",
    items: [
      "SA100 & supplementary pages",
      "Income & expense optimisation",
      "Capital gains review",
      "HMRC correspondence handled",
    ],
  },
  {
    icon: BookOpen,
    title: "Bookkeeping",
    desc: "Clean, real-time books in Xero, QuickBooks or FreeAgent — never a year-end scramble again.",
    items: [
      "Receipt capture via Dext",
      "Bank reconciliation",
      "Monthly management reports",
      "Cash flow visibility",
    ],
  },
  {
    icon: Receipt,
    title: "VAT Returns",
    desc: "Quarterly VAT returns submitted under Making Tax Digital — flat rate or standard.",
    items: [
      "MTD-compliant filing",
      "Scheme suitability review",
      "VAT registration support",
      "Reverse charge & EU rules",
    ],
  },
  {
    icon: HardHat,
    title: "CIS (Construction)",
    desc: "Contractor and subcontractor CIS support — stay compliant and reclaim what you’re owed.",
    items: [
      "Monthly CIS returns",
      "Subcontractor verification",
      "CIS refund claims",
      "Gross status applications",
    ],
  },
  {
    icon: Users,
    title: "Payroll & MTD Ready",
    desc: "Fully managed RTI payroll plus a clear path to Making Tax Digital for Income Tax.",
    items: [
      "Weekly or monthly RTI submissions",
      "Payslips & P60s",
      "Auto-enrolment pensions",
      "MTD ITSA quarterly updates",
    ],
  },
];

export const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="bg-white py-20 md:py-28"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C57A5A] font-medium">
            Services
          </p>
          <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl text-[#2A3430] leading-tight">
            One firm. Every accounting need.
          </h2>
        </div>
        <p className="text-[#2A3430]/80 md:max-w-md">
          Pick what you need today, layer on more as you grow. Everything is
          delivered by qualified UK accountants — never offshored.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map(({ icon: Icon, title, desc, items }) => (
          <div
            key={title}
            data-testid={`service-card-${title.toLowerCase().replace(/\s|&|\(|\)/g, "-")}`}
            className="group bg-white border border-[#2A3430]/10 rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#C57A5A]/10 hover:border-[#C57A5A]/50"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5EEE9] text-[#C57A5A] group-hover:bg-[#C57A5A] group-hover:text-white transition-colors duration-300">
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <h3 className="font-serif-display mt-5 text-2xl text-[#2A3430]">
              {title}
            </h3>
            <p className="mt-2 text-sm text-[#2A3430]/80 leading-relaxed">
              {desc}
            </p>
            <ul className="mt-5 space-y-2.5">
              {items.map((it) => (
                <li key={it} className="flex items-start gap-2.5 text-sm text-[#2A3430]">
                  <Check className="mt-0.5 h-4 w-4 text-[#C57A5A] shrink-0" strokeWidth={2.5} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
